import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import { Formik, Form } from 'formik'
import { Close } from '@material-ui/icons'
import 'react-phone-input-2/lib/material.css'
import { Autocomplete } from '@material-ui/lab'
import { useSelector } from 'react-redux'
import { equals, isEmpty, isNil, lensPath, view } from 'ramda'
import { Dialog, Typography, makeStyles, IconButton } from '@material-ui/core'

import { isEmptyOrNil } from 'libs/empty'
import effects from 'app/effects/effects'
import { disableSpaceBar } from 'commons/keyDown'
import { STATUS_INVITED } from 'commons/useTransfer'
import { allLanguages } from 'commons/fetchLanguages'
import { useIsRootAdmin } from 'commons/useUserProfile'
import { getFullPhoneNumber } from 'commons/phoneNumber'

import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import InformationDialog from 'app/componentV4/Dialog/InformationDialog'

import Avatars from 'app/componentV4/Avatar/Avatars'
import { SECONDARY } from 'app/constants/button'
import { checkEmail, emailType } from 'commons/checkEmail'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import Transfer from 'app/main/settings/teamMembersV4/Action/Transfer'
import TransferWithPasswordOrOTP from 'app/main/settings/teamMembersV4/Action/TransferWithPasswordOrOTP'
import { validationSchema } from 'app/main/settings/teamMembersV4/validation'
import CancelTransfer from 'app/main/settings/teamMembersV4/Action/CancelTransfer'
import RolesSelector from 'app/main/settings/teamMembersV4/components/RolesSelector'
import { ACTIVE } from 'app/main/settings/teamMembersV4/constants'
import PhoneNumberInputV4 from 'app/componentV4/Input/PhoneNumberInputV4'
import useFeatureFlag from 'app/configs/useFeatureFlag'

const useStyles = makeStyles((themes) => ({
  root: {
    '& .MuiDialog-paper': {
      width: '520px',
      maxHeight: 'calc(100vh - 180px)',
      overflowX: 'auto',
      padding: '3rem',
    },
  },
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
  submit: {
    '&.MuiButton-contained.Mui-disabled': {
      backgroundColor: `${themes.colors['grey-100']} !important`,
    },
  },
}))

const TeamMemberDialog = ({
  open,
  onClose,
  onSubmitted,
  initialData = {},
  isSubmitting = false,
}) => {
  const { mes_4165_teammember_tranfer_otp_password } = useFeatureFlag()
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isRootAdmin = useIsRootAdmin()
  const { selectedPlace, data } = useSelector(({ auth }) => auth.user)

  const isPending = equals(
    view(lensPath(['transferRequestTo', 'status']))(selectedPlace),
    STATUS_INVITED,
  )
  const isPendingCurrent = equals(
    view(lensPath(['transferRequestTo', 'user', 'id']))(selectedPlace),
    initialData?.id,
  )
  const transferId = view(lensPath(['transferRequestTo', 'id']))(selectedPlace)
  const isTransfer = isRootAdmin(data.id) && !isPending

  const [roles, setRoles] = useState([])
  const [isEdit, setIsEdit] = useState(false)
  const [langauge, setLangauge] = useState([])
  const [isConfirm, setIsConfirm] = useState(false)
  const [isEditRootAdmin, setIsEditRootAdmin] = useState(false)

  const getLanguages = async () => {
    const language = await allLanguages()
    setLangauge(language)
  }
  const getRoles = async () => {
    return effects.apis
      .getRoles({
        page: 1,
        limit: 10000,
        sort: 'name:ASC',
      })
      .then((response) => setRoles(response.data))
  }

  const handleEmailChange = useCallback(
    async (value, errors, setFieldError) => {
      const isError = Boolean(errors.email)

      const trim = value.trim()
      if (isEdit && trim === initialData?.email) return
      if (!isError && !isEmpty(value)) {
        const checkingEmail = await checkEmail(
          formatMessage,
          trim,
          emailType.official,
        )
        setFieldError('email', checkingEmail)
      }
    },
    [formatMessage, initialData?.email, isEdit],
  )

  const handleInitialLanguage = useCallback(() => {
    if (isNil(initialData?.languageId)) return null
    else return langauge.find((option) => option.id === initialData?.languageId)
  }, [initialData?.languageId, langauge])

  const handleOnClose = () => {
    onClose()
  }

  const handleDisplayName = (data) => {
    if (isEmptyOrNil(data))
      return formatMessage({ id: 'teamMembers.lable.add.teamMembers' })
    else return `${data.firstName} ${data.lastName}`
  }

  const handleRoleIds = useCallback((data) => {
    if (isNil(data)) return []
    return data?.map((item) => item.id)
  }, [])

  const handleCreateMode = useCallback(() => {
    setIsEdit(false)
    setIsEditRootAdmin(false)
  }, [])

  useEffect(() => {
    getLanguages()
    getRoles()
  }, [])

  useEffect(() => {
    if (!isNil(initialData?.id) && open) {
      setIsEdit(true)
      setIsEditRootAdmin(isRootAdmin(initialData?.id))
    } else if (isNil(initialData?.id) && open) {
      handleCreateMode()
    }
  }, [handleCreateMode, initialData, isRootAdmin, open])

  return (
    <Dialog
      open={open}
      // onClose={onClose}
      className={classes.root}
      aria-labelledby="customized-dialog-title"
    >
      <div className="w-full h-96 bg-primary-200 absolute top-0 left-0"></div>
      <div className="flex justify-between z-10">
        <div className="flex">
          <Avatars
            id={initialData?.id ?? 0}
            name={handleDisplayName(initialData)}
            src={initialData?.avatar}
            characters={initialData?.avatarCharacters ?? ''}
            variant="square"
            className="w-96 h-96 text-36 font-bold rounded-8"
          />
          <Typography className="text-18 font-bold text-primary my-auto ml-10 mr-0">
            {handleDisplayName(initialData)}
          </Typography>
        </div>
        <div>
          <IconButton aria-label="close" onClick={handleOnClose}>
            <Close fontSize="small" />
          </IconButton>
        </div>
      </div>

      <Formik
        initialValues={{
          firstName: initialData?.firstName ?? '',
          lastName: initialData?.lastName ?? '',
          languageId: handleInitialLanguage(),
          roles: handleRoleIds(initialData?.roles),
          email: initialData?.email ?? '',
          phoneNumber:
            getFullPhoneNumber(
              initialData?.countryCode,
              initialData?.phoneNumber,
            ) ?? '',
          countryCode: initialData?.countryCode ?? '',
        }}
        validationSchema={validationSchema(formatMessage)}
        onSubmit={async (values) => {
          await onSubmitted(values)
        }}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          handleBlur,
          handleChange,
          setFieldValue,
          setFieldError,
        }) => (
          <Form>
            <div className="mt-36">
              <LabelInput
                label={formatMessage({
                  id: 'firstName.label',
                })}
                required
              >
                <SimpleInput
                  className={classes.input}
                  id="team-member-dialog-input-firstName"
                  name="firstName"
                  placeholder={formatMessage({
                    id: 'firstName.label',
                  })}
                  value={values.firstName}
                  size="medium"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  inputProps={{ maxLength: 40 }}
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  maxLength={40}
                  disabled={isEditRootAdmin}
                />
              </LabelInput>

              <LabelInput
                label={formatMessage({
                  id: 'lastName.label',
                })}
                required
              >
                <SimpleInput
                  className={classes.input}
                  id="team-member-dialog-input-lastName"
                  name="lastName"
                  placeholder={formatMessage({
                    id: 'lastName.label',
                  })}
                  value={values.lastName}
                  size="medium"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  inputProps={{ maxLength: 40 }}
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  maxLength={40}
                  disabled={isEditRootAdmin}
                />
              </LabelInput>

              <LabelInput
                label={formatMessage({
                  id: 'languageSelector.label',
                })}
                required
              >
                <Autocomplete
                  id="team-member-dialog-autocomplete-language"
                  name="languageId"
                  options={langauge}
                  onChange={(event, selectedOptions, reason, { option }) => {
                    setFieldValue('languageId', option)
                  }}
                  onBlur={handleBlur}
                  getOptionLabel={(option) =>
                    `${option.nativeName} - ${option.name}`
                  }
                  getOptionSelected={(option, value) => option.id === value.id}
                  value={values.languageId}
                  renderOption={(option) => (
                    <div>
                      <Typography className="text-12 text-primary ">
                        {option.nativeName}
                      </Typography>
                      <Typography className="text-12 text-primary font-bold">
                        {option.name}
                      </Typography>
                    </div>
                  )}
                  disabled={isEditRootAdmin}
                  renderInput={(params) => (
                    <div ref={params.InputProps.ref} className="w-full">
                      <SimpleInput
                        className={classes.input}
                        id="team-member-dialog-input-language"
                        placeholder={formatMessage({
                          id: 'languageSelector.label',
                        })}
                        size="medium"
                        fullWidth
                        error={touched.languageId && Boolean(errors.languageId)}
                        helperText={touched.languageId && errors.languageId}
                        {...params}
                      />
                    </div>
                  )}
                />
              </LabelInput>

              <LabelInput
                label={formatMessage({
                  id: 'memberForm.roles.label',
                })}
                required
              >
                <RolesSelector
                  placeholder={formatMessage({
                    id: 'memberForm.roles.label',
                  })}
                  id="team-member-dialog-input-roles"
                  name="roles"
                  teamMemberName={`${values.firstName} ${values.lastName}`}
                  className="w-full"
                  emptyLabelId="error.roles.notFound"
                  value={values.roles}
                  defaultValue={initialData?.roles}
                  multiple
                  onChange={(value) => {
                    setFieldValue('roles', value)
                  }}
                  options={roles}
                  error={touched.roles && Boolean(errors.roles)}
                  helperText={touched.roles && errors.roles}
                  disabled={isEditRootAdmin}
                />
              </LabelInput>

              <LabelInput
                label={formatMessage({
                  id: 'email.address.label',
                })}
                required
              >
                <SimpleInput
                  className={classes.input}
                  id="team-member-dialog-input-email"
                  name="email"
                  placeholder={formatMessage({
                    id: 'email.address.label',
                  })}
                  value={values.email}
                  size="medium"
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e)
                    handleEmailChange(e.target.value, errors, setFieldError)
                  }}
                  fullWidth
                  inputProps={{
                    onKeyDown: (e) => {
                      disableSpaceBar(e)
                    },
                  }}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  disabled={isEditRootAdmin}
                />
              </LabelInput>

              <LabelInput
                label={formatMessage({
                  id: 'phone.label',
                })}
              >
                <PhoneNumberInputV4
                  value={values.phoneNumber}
                  id="team-member-dialog-input-phone-number"
                  name="phoneNumber"
                  onChange={(number) => {
                    setFieldValue('phoneNumber', number)
                  }}
                  error={errors.phoneNumber && touched.phoneNumber}
                  helperText={errors.phoneNumber}
                  disabled={isEditRootAdmin}
                />
              </LabelInput>
            </div>
            {isEdit && isTransfer && initialData?.status === ACTIVE && (
              <div className="w-full py-10 px-5 bg-primary-200 rounded-lg space-y-5">
                <Typography className="text-14 text-neutrals-700">
                  {formatMessage(
                    {
                      id: 'teamMembers.transfer.title',
                    },
                    { fullName: `${values.firstName} ${values.lastName}` },
                  )}
                </Typography>
                {mes_4165_teammember_tranfer_otp_password ? (
                  <TransferWithPasswordOrOTP
                    id={initialData?.id}
                    name={`${values.firstName} ${values.lastName}`}
                    email={data.email}
                  />
                ) : (
                  <Transfer
                    id={initialData?.id}
                    name={`${values.firstName} ${values.lastName}`}
                    onSuccess={setIsConfirm}
                  />
                )}
              </div>
            )}

            <InformationDialog
              open={isConfirm}
              onClose={() => setIsConfirm(false)}
              title={formatMessage({
                id: 'transfer.success.title',
              })}
            >
              <div className="my-5">
                <Typography className="text-md text-primary text-center">
                  <FormattedMessage
                    id="transfer.success.content"
                    values={{
                      teamMemberName: `${values.firstName} ${values.lastName}`,
                      b: (text) => <b>{text}</b>,
                    }}
                  />
                </Typography>
              </div>
            </InformationDialog>

            {isEdit && isPending && isPendingCurrent && (
              <div className="w-full py-10 px-5 bg-primary-200 rounded-lg space-y-5">
                <Typography className="text-14 text-neutrals-700">
                  {formatMessage(
                    {
                      id: 'teamMembers.transfer.title',
                    },
                    { fullName: `${values.firstName} ${values.lastName}` },
                  )}
                </Typography>
                <CancelTransfer id={values.id} transferId={transferId} />
              </div>
            )}
            <div className="flex justify-center mt-10">
              <ContainedButton
                id="update-teammember-btn"
                type="submit"
                color={SECONDARY}
                isLoading={isSubmitting}
                disabled={isSubmitting || isEditRootAdmin || (isEdit && !dirty)}
              >
                {formatMessage({
                  id: 'action.save',
                })}
              </ContainedButton>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

TeamMemberDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitted: PropTypes.func,
  isSubmitting: PropTypes.bool,
  initialData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    languageId: PropTypes.number,
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
}

export default TeamMemberDialog
